import { ArrowUpRight, Check, Zap, Megaphone, Smartphone, BarChart3, Users, TrendingUp, Shield, Clock, Globe, Building2, ShoppingBag, GraduationCap, Heart, Plane, Calendar, CreditCard, Radio, Box, Mail, Target, MessageSquare, DollarSign, FileText, Layers, Send, Percent, Archive, CheckCircle, AlertCircle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import { useState } from 'react';

const features = [
  { icon: FileText, title: '100+ Professional Templates', text: 'Choose from industry-specific SMS templates optimized for 160 characters. Customize with your brand and variables.' },
  { icon: Percent, title: 'Volume Discount Pricing', text: 'Automatic tiered discounts: 10% off for 10,000+ messages, 15% off for 50,000+, 20% off for 100,000+ messages.' },
  { icon: Target, title: 'Advanced Personalization', text: 'Use variables like {{name}}, {{order_id}}, {{date}} to personalize messages. Import contacts or use workspace personas.' },
  { icon: Calendar, title: 'Recurring Schedules', text: 'Set up automated recurring messages for newsletters, reminders, and promotions. Schedule daily, weekly, or monthly.' },
  { icon: Archive, title: 'Draft Management', text: 'Save campaign drafts and continue later. Store multiple draft versions and manage them from one dashboard.' },
  { icon: BarChart3, title: 'Real-time Analytics', text: 'Track delivery rates, open rates, click rates, and spending. View performance by campaign, date, or segment.' },
];

const benefits = [
  { title: '98% delivery rate globally', text: 'Our SMS delivery rate exceeds 98% globally. We use premium routes and infrastructure to ensure your messages reach recipients reliably and quickly.' },
  { title: 'Volume discounts up to 20%', text: 'The more you send, the more you save. Automatic discounts applied at checkout: 10% for 10,000+ messages, 15% for 50,000+, 20% for 100,000+.' },
  { title: 'Industry-specific templates', text: 'Choose from 100+ professional SMS templates across 12 industries including retail, healthcare, education, finance, and more.' },
  { title: 'Community guidelines compliance', text: 'Built-in community guidelines ensure your SMS campaigns comply with anti-spam regulations and best practices for SMS marketing.' },
];

const useCases = [
  { title: 'Order confirmations & shipping updates', text: 'Send automated order confirmations, shipping notifications, and delivery updates via SMS. Keep customers informed throughout their purchase journey.' },
  { title: 'Appointment reminders & confirmations', text: 'Reduce no-shows with automated appointment reminders and confirmations. Healthcare, salons, consultants, and service providers benefit from timely reminders.' },
  { title: 'Marketing campaigns & promotions', text: 'Send targeted marketing messages, promotional offers, and product announcements to opted-in customers using SMS campaigns.' },
  { title: 'Customer support & notifications', text: 'Handle customer inquiries, troubleshooting, and support via SMS. Provide real-time assistance with two-way messaging.' },
];

const industries = [
  { title: 'E-commerce & Retail', text: 'Recover 25% more abandoned carts with SMS reminders. Send order updates, delivery notifications, and promotional offers.' },
  { title: 'Healthcare & Medical', text: 'Reduce no-shows by 60% with automated appointment reminders via SMS. Send health tips and follow-up messages.' },
  { title: 'Financial Services', text: 'Improve customer engagement by 50% with secure transaction alerts via SMS. Send OTP and verification codes.' },
  { title: 'Education', text: 'Increase course enrollment by 40% with targeted SMS campaigns. Share exam reminders and study materials.' },
];

const pricing = [
  { tier: 'Pay-As-You-Go', price: '$0.008', unit: 'per message', features: ['No monthly commitment', '98% delivery rate', 'Global coverage', 'Real-time analytics', 'Template library access'] },
  { tier: 'Volume Discount', price: '10-20%', unit: 'savings', features: ['10% off for 10,000+ messages', '15% off for 50,000+ messages', '20% off for 100,000+ messages', 'Priority support', 'Advanced analytics'], highlight: true },
  { tier: 'Enterprise', price: 'Custom', unit: 'pricing', features: ['Dedicated account manager', 'Custom sender ID registration', 'API access', '24/7 support', 'SLA guarantees'] },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'What is bulk SMS marketing?',
    answer: 'Bulk SMS marketing is sending large volumes of text messages to multiple recipients simultaneously. It\'s used for promotions, alerts, notifications, and customer engagement campaigns.',
  },
  {
    question: 'How does volume discount pricing work?',
    answer: 'Our tiered pricing offers automatic discounts: 10% off for 10,000+ messages, 15% off for 50,000+ messages, and 20% off for 100,000+ messages. The more you send, the more you save.',
  },
  {
    question: 'Do I need to register a sender ID?',
    answer: 'Yes. For professional SMS delivery, you can register your business sender ID in your Moments dashboard. The registration process is simple and we guide you through it step by step.',
  },
  {
    question: 'Can I personalize SMS messages?',
    answer: 'Absolutely. Use our template variables like {{name}}, {{order_id}}, and {{date}} to personalize messages at scale. Import contacts from CSV or select from your workspace personas.',
  },
  {
    question: 'What is the delivery rate for bulk SMS?',
    answer: 'Our SMS delivery rate exceeds 98% globally. We use premium routes and infrastructure to ensure your messages reach recipients reliably and quickly.',
  },
  {
    question: 'Can I schedule SMS campaigns?',
    answer: 'Yes. Schedule your bulk SMS campaigns for specific dates and times. Set up recurring messages for weekly newsletters, monthly promotions, or automated reminders.',
  },
  {
    question: 'How do I track SMS campaign performance?',
    answer: 'View real-time analytics including delivery rates, open rates, click rates, and cost analysis. Track performance by campaign, date, or recipient segment.',
  },
  {
    question: 'What industries use bulk SMS?',
    answer: 'E-commerce, healthcare, education, real estate, financial services, travel, and hospitality all use bulk SMS for customer engagement, notifications, and marketing.',
  },
];

export default function BulkMessaging() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  return (
    <>
      <SEO
        title="Bulk SMS Marketing Platform | Send 100,000+ Messages with Volume Discounts"
        description="Professional bulk SMS marketing with 98% delivery rates, 100+ templates, volume discounts up to 20%, and advanced personalization. Perfect for e-commerce, healthcare, education, and more."
        path="/bulk-messaging"
        keywords="bulk SMS, SMS marketing, text message marketing, bulk SMS service, SMS campaign, SMS templates, volume discount SMS, SMS automation, business SMS, SMS API"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-1.5 text-sm font-semibold text-blue-300 backdrop-blur">
                <CheckCircle className="h-4 w-4" /> 98% delivery rate • 100+ templates
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Professional bulk <span className="gradient-text">SMS marketing</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Send SMS campaigns at scale with industry-specific templates, automatic volume discounts up to 20%, and advanced personalization. Perfect for promotions, notifications, and customer engagement.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="#pricing"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-blue-500 hover:bg-slate-800"
                >
                  View Pricing <BarChart3 className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-blue-400/10 blur-3xl" />
              <div className="relative overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/60 p-2 backdrop-blur">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/ABC_SMS.webp"
                  alt="Bulk SMS marketing platform showing campaign dashboard and analytics"
                  className="rounded-2xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Enterprise bulk SMS features</h2>
            <p className="section-subtitle mx-auto mt-4">Professional features designed to maximize your SMS campaign performance and ROI.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why businesses choose bulk SMS</h2>
            <p className="section-subtitle mx-auto mt-4">Advantages that set our bulk SMS platform apart from email and other channels.</p>
          </div>
          <div className="grid gap-8 md:grid-cols-2">
            {benefits.map((b) => (
              <div key={b.title} className="flex gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
                  <Check className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">{b.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{b.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="use-cases-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="use-cases-heading" className="section-title">Bulk SMS use cases for your business</h2>
            <p className="section-subtitle mx-auto mt-4">Practical applications of bulk SMS across different business scenarios.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <Megaphone className="h-6 w-6" />
                </div>
                <h3 className="text-base font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="industries-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="industries-heading" className="section-title">Industries powered by bulk SMS</h2>
            <p className="section-subtitle mx-auto mt-4">From e-commerce to healthcare, businesses across all industries use bulk SMS.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {industries.map((i) => (
              <div key={i.title} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <Building2 className="h-6 w-6" />
                </div>
                <h3 className="text-base font-bold text-slate-900">{i.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{i.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="py-24" aria-labelledby="pricing-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="pricing-heading" className="section-title">Simple pricing with volume discounts</h2>
            <p className="section-subtitle mx-auto mt-4">Pay as you go with automatic volume discounts. The more you send, the more you save.</p>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {pricing.map((p) => (
              <div key={p.tier} className={`rounded-3xl border ${p.highlight ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'} p-8 shadow-sm`}>
                <h3 className="text-lg font-bold text-slate-900">{p.tier}</h3>
                <p className="mt-2 text-3xl font-extrabold text-slate-900">{p.price} <span className="text-lg font-normal text-slate-600">{p.unit}</span></p>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-slate-600">
                      <CheckCircle className="h-5 w-5 shrink-0 text-blue-600" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`mt-8 block w-full rounded-full px-6 py-3 text-center text-base font-semibold transition ${p.highlight ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
                >
                  Get Started
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about bulk SMS marketing.</p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.question} className="rounded-2xl border border-slate-200 bg-white">
                <button
                  onClick={() => setOpenFaq(openFaq === faq.question ? null : faq.question)}
                  className="flex w-full items-center justify-between p-6 text-left"
                >
                  <span className="text-base font-semibold text-slate-900">{faq.question}</span>
                  {openFaq === faq.question ? (
                    <ChevronUp className="h-5 w-5 text-slate-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-slate-500" />
                  )}
                </button>
                {openFaq === faq.question && (
                  <div className="px-6 pb-6">
                    <p className="text-sm text-slate-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-950 py-24 text-white">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
            Ready to send your first bulk SMS campaign?
          </h2>
          <p className="mt-4 text-lg text-slate-300">
            Start your free trial today with 100+ professional templates and automatic volume discounts up to 20%.
          </p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:justify-center">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100"
            >
              Get Started Free <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
